import { useForm, Control } from 'react-hook-form';
import { isCleanText } from '@/utils/sanitize';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Lock } from 'lucide-react';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';

export interface DepartmentFormData {
  group_key: string;
  group_name: string;
  group_description: string;
  parent_group_key: string;
}

interface DepartmentFormProps {
  initialData?: Partial<DepartmentFormData>;
  onSubmit: (data: DepartmentFormData) => void;
  isSubmitting: boolean;
  isEditing?: boolean;
  onCancel: () => void;
}

// With yupResolver the Input/Textarea clean-text rule is skipped, so the schema keeps the check
const departmentSchema = yup.object({
  group_key: yup
    .string()
    .required('Department key is required')
    .matches(/^[A-Z_]+$/, 'Department key must contain only uppercase letters and underscores'),
  group_name: yup.string().required('Department name is required').trim()
    .test('clean-text', 'Please use only letters, numbers and standard punctuation', (v) => isCleanText(v)),
  group_description: yup.string().default('')
    .test('clean-text', 'Please use only letters, numbers and standard punctuation', (v) => isCleanText(v)),
  parent_group_key: yup.string().default('root')
}).required();

export default function DepartmentForm({
  initialData,
  onSubmit,
  isSubmitting,
  isEditing = false,
  onCancel
}: DepartmentFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    control
  } = useForm<DepartmentFormData>({
    resolver: yupResolver(departmentSchema) as any,
    // Validate while typing so the clean-text error shows under the field, not only after Save
    mode: 'onChange',
    defaultValues: {
      group_key: initialData?.group_key || '',
      group_name: initialData?.group_name || '',
      group_description: initialData?.group_description || '',
      parent_group_key: initialData?.parent_group_key || 'root'
    }
  });
  const formControl = control as Control<DepartmentFormData>;

  const handleGroupKeyInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z_]/g, '');
    setValue('group_key', value);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Department Key <span className="text-red-500">*</span>
        </label>
        {isEditing ? (
          <div className="relative">
            <input
              type="text"
              value={watch('group_key')}
              className="w-full px-3 py-2 pr-10 border border-gray-200 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
              disabled
            />
            <Lock className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          </div>
        ) : (
          <input
            type="text"
            {...register('group_key')}
            onChange={handleGroupKeyInput}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
              errors.group_key ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
            }`}
            placeholder="e.g., SALES_TEAM or IT_DEPARTMENT"
          />
        )}
        {errors.group_key && (
          <p className="text-red-500 text-sm mt-1">{errors.group_key.message}</p>
        )}
        {!isEditing && (
          <p className="text-xs text-gray-500 mt-1">
            Only uppercase letters (A-Z) and underscores (_) allowed
          </p>
        )}
        {isEditing && (
          <p className="text-xs text-gray-500 mt-1">
            Department key cannot be changed after creation
          </p>
        )}
      </div>

      <Input<DepartmentFormData>
        name="group_name"
        control={formControl}
        label="Department Name"
        required
        type="text"
        placeholder="e.g., Sales Team"
      />

      <Textarea<DepartmentFormData>
        name="group_description"
        control={formControl}
        label="Description"
        rows={3}
        placeholder="Enter department description..."
      />

      <div className="flex justify-end gap-3 mt-6">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
          disabled={isSubmitting}
        >
          {isSubmitting ? (isEditing ? 'Updating...' : 'Creating...') : (isEditing ? 'Update Department' : 'Create Department')}
        </button>
      </div>
    </form>
  );
}